import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Circle, Check } from 'lucide-react';
import { constant } from '../../config/constant';
import { useEffect, useState } from 'react';
import { visit } from 'unist-util-visit'; // ⬅️ Required for plugin
import ScrollableTable from '../common/ScrollableTable';
import FeedbackModal from '../ui/FeedbackModal';

const { ASSISTANT } = constant;


// ✅ Custom plugin to inject `data-label` into each <td>
function rehypeInjectDataLabel() {
  return (tree) => {
    let headers = [];

    visit(tree, 'element', (node) => {
      if (node.tagName === 'thead') {
        headers = [];
        for (const child of node.children) {
          if (child.tagName === 'tr') {
            for (const th of child.children) {
              if (th.tagName === 'th') {
                const text = th.children?.[0]?.value || '';
                headers.push(text);
              }
            }
          }
        }
      }

      // Apply data-labels to <td>
      if (node.tagName === 'tr' && node.children?.length && headers.length) {
        node.children.forEach((tdNode, index) => {
          if (tdNode.tagName === 'td') {
            tdNode.properties = {
              ...tdNode.properties,
              ['data-label']: headers[index] || '',
            };
          }
        });
      }
    });
  };
}

export default function ChatMessage({
  text,
  sender,
  isRespLoading,
  isTyping = false,
  isStreaming = false,
  isTerminated = false,
  scrollRef,
  chatId,
  messageId,
  userMessageId,
  userMessage,
  assistantMessageId,
}) {
  const isAssistant = sender === ASSISTANT;
  const [displayedText, setDisplayedText] = useState(isTyping ? '' : text);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Update displayedText when text changes (for streaming)
  useEffect(() => {
    if (isStreaming) {
      setDisplayedText(text);
    }
  }, [text, isStreaming]);

  // Typewriter effect (only for non-streaming messages)
  useEffect(() => {
    if (!isAssistant || !isTyping || isStreaming) return;

    const chars = Array.from(text); // handles special characters
    let i = 0;
    const speed = 8; // milliseconds between characters
    setDisplayedText(''); // reset before typing

    const interval = setInterval(() => {
      setDisplayedText((prev) => {
        const next = prev + chars[i];
        i++;

        if (scrollRef.current) {
          scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }

        if (i >= chars.length) clearInterval(interval);

        return next;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [isAssistant, isTyping, isStreaming]);

  const handleFeedbackSubmitSuccess = () => {
    setFeedbackSubmitted(true);
  };

  return (
    <>
      <div className={`flex flex-col ${isAssistant ? 'items-start' : 'items-end'}`}>
        <div
          className={`rounded-3xl font-normal text-base text-wrap ${
            isAssistant
              ? 'p-2 bg-transparent text-[#222525] leading-[30px]'
              : 'px-4 py-2 bg-[#F9F9F9] text-[#002231] max-w-[600px]'
          }`}
          // style={{ wordBreak: 'break-word' }}
        >
          {isRespLoading && displayedText === '' ? (
            <div className="flex gap-1">
              {[0.2, 0.4, 0.6].map((delay, index) => (
                <div
                  key={index}
                  className="animate-bounce"
                  style={{ animationDelay: `${delay}s` }}
                >
                  <Circle className="fill-gray-400 size-2" />
                </div>
              ))}
            </div>
          ) : isAssistant ? (
            <div className="markdown-preview">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw,rehypeInjectDataLabel]}
                components={{
                  a: ({ href, children, ...props }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {
                      children
                      }
                    </a>
                  ),
                  table: ({ children, ...props }) => (
                    <ScrollableTable>
                      <table {...props}>{children}</table>
                    </ScrollableTable>
                  ),
                }}
              >
                {displayedText}
              </ReactMarkdown>
              {isTerminated && (
                <div className="mt-2 text-xs text-red-500 italic">
                  ⚠️ Response terminated due to connection error
                </div>
              )}
            </div>
          ) : (
            text
          )}
        </div>

        {/* Feedback Link - Only show for assistant messages */}
        {isAssistant && !isRespLoading && displayedText && (
          <div className="mt-2 ml-2">
            {feedbackSubmitted ? (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Check size={14} />
                <span>Feedback submitted</span>
              </div>
            ) : (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-black bg-[#f7f7f7] border border-gray-300 rounded-md shadow-sm transition-colors hover:bg-[#ebebeb] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0096ae]"
              >
                Provide feedback
              </button>
            )}
          </div>
        )}

        {/* Scroll reference for auto-scrolling - placed outside message content */}
        {scrollRef && <div ref={scrollRef} />}
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          open={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          chatId={chatId}
          userMessageId={userMessageId}
          userMessage={userMessage}
          assistantMessageId={assistantMessageId || messageId}
          assistantMessage={text}
          onSubmitSuccess={handleFeedbackSubmitSuccess}
        />
      )}
    </>
  );
}
